import { Moment } from 'moment';
import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { DatePicker } from 'components/atoms/date-picker';
import { Form } from 'components/atoms/form';
import { Modal } from 'components/atoms/modal';
import { OutlineError } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';

const DatePickerWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: var(--spacing-05);
  ${Form.Item} {
    width: 100%;
    ${DatePicker} {
      width: 100%;
    }
  }
`;

const StyledForm = styled(Form)`
  .ant-form-item-has-error .ant-picker {
    ${OutlineError};
    &:focus {
      box-shadow: none;
      ${OutlineError};
    }
  }
`;

interface Props {
  visible: boolean;
  onExportFinished: () => void;
  onCancel: () => void;
  exportHandler: (startDate: Moment, endDate: Moment) => Promise<any>;
}

export const ExportAsCSVModal: FC<Props> = ({
  visible,
  onExportFinished,
  onCancel,
  exportHandler,
}) => {
  const [form] = Form.useForm();
  const requiredMessage = useTranslation('input.required');
  const [exportInProgress, setExportInProgress] = useState(false);
  const onFormSubmitted = useCallback(() => {
    form.validateFields().then(() => {
      setExportInProgress(true);
      exportHandler(
        form.getFieldValue('startDate'),
        form.getFieldValue('endDate'),
      )
        .then(() => {
          onExportFinished();
          form.resetFields();
        })
        .finally(() => setExportInProgress(false));
    });
  }, [exportHandler, form, onExportFinished]);
  const cancel = useCallback(() => {
    onCancel();
    form.resetFields();
  }, [form, onCancel]);

  return (
    <Modal
      visible={visible}
      okText={<FormattedMessage id="export" />}
      okButtonProps={{
        loading: exportInProgress,
      }}
      onOk={onFormSubmitted}
      onCancel={cancel}
      title={<FormattedMessage id="resource_management.export.modal.title" />}
      destroyOnClose
    >
      <StyledForm layout="vertical" form={form}>
        <DatePickerWrapper>
          <Form.Item
            label={<FormattedMessage id="start_date" />}
            required
            name="startDate"
            rules={[
              {
                required: true,
                message: requiredMessage,
              },
            ]}
            shouldUpdate
          >
            <DatePicker
              autoFocus
              allowClear={false}
              disabledDate={(date) => {
                const endDate = form.getFieldValue('endDate');

                return endDate != null && date.isAfter(endDate, 'day');
              }}
            />
          </Form.Item>
          <Form.Item
            label={<FormattedMessage id="end_date" />}
            required
            name="endDate"
            rules={[
              {
                required: true,
                message: requiredMessage,
              },
            ]}
            shouldUpdate
          >
            <DatePicker
              allowClear={false}
              disabledDate={(date) => {
                const startDate = form.getFieldValue('startDate');

                return startDate != null && date.isBefore(startDate, 'day');
              }}
            />
          </Form.Item>
        </DatePickerWrapper>
      </StyledForm>
    </Modal>
  );
};
